import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  COMPANY_DOCUMENTS_LANGUAGE_STORAGE_KEY,
  DEFAULT_COMPANY_DOCUMENTS_LANGUAGE,
  normalizeCompanyDocumentsLanguage,
  translateCompanyDocuments,
} from "./translations";

const CompanyDocumentsLanguageContext =
  createContext(null);

const getInitialLanguage = () => {
  if (typeof window === "undefined") {
    return DEFAULT_COMPANY_DOCUMENTS_LANGUAGE;
  }

  try {
    return normalizeCompanyDocumentsLanguage(
      window.localStorage.getItem(
        COMPANY_DOCUMENTS_LANGUAGE_STORAGE_KEY,
      ),
    );
  } catch {
    return DEFAULT_COMPANY_DOCUMENTS_LANGUAGE;
  }
};

export function CompanyDocumentsLanguageProvider({
  children,
}) {
  const [language, setLanguageState] =
    useState(getInitialLanguage);

  const setLanguage = useCallback(
    (value) => {
      setLanguageState(
        normalizeCompanyDocumentsLanguage(
          value,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        COMPANY_DOCUMENTS_LANGUAGE_STORAGE_KEY,
        language,
      );
    } catch {
      return;
    }
  }, [language]);

  const t = useCallback(
    (key, params) =>
      translateCompanyDocuments(
        language,
        key,
        params,
      ),
    [language],
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [
      language,
      setLanguage,
      t,
    ],
  );

  return (
    <CompanyDocumentsLanguageContext.Provider
      value={value}
    >
      {children}
    </CompanyDocumentsLanguageContext.Provider>
  );
}

export function useCompanyDocumentsLanguage() {
  const context = useContext(
    CompanyDocumentsLanguageContext,
  );

  if (!context) {
    return {
      language:
        DEFAULT_COMPANY_DOCUMENTS_LANGUAGE,
      setLanguage: () => {},
      t: (key, params) =>
        translateCompanyDocuments(
          DEFAULT_COMPANY_DOCUMENTS_LANGUAGE,
          key,
          params,
        ),
    };
  }

  return context;
}
